import {
  FC,
  ReactNode,
  MouseEvent,
  useState,
  useCallback,
  MouseEventHandler,
  ReactElement,
  CSSProperties,
} from 'react';
import styled from 'styled-components';
import { Button, Flex } from 'antd';
import * as Icon from 'react-feather';
import { Placement } from '@floating-ui/react';
import { colorError } from '@atlas-frontend/design-tokens';
import { Popover } from './Popover';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './Popconfirm.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory('popconfirm');
addTranslations(translations);

interface PopconfirmPropsBase {
  id: string;
  title: ReactNode;
  placement?: Placement | 'auto';
  description?: ReactNode;
  okText?: ReactNode;
  cancelText?: ReactNode;
  onConfirm?: (event: MouseEvent<HTMLElement>) => boolean | void;
  onCancel?: (event: MouseEvent<HTMLElement>) => boolean | void;
  icon?: ReactNode;
  wrapperType?: 'span' | 'div';
  style?: CSSProperties;
  stopClickPropagation?: boolean;
  children: ReactElement<{ onClick?: MouseEventHandler<HTMLElement> }>;
}

export type PopconfirmProps = PopconfirmPropsBase &
  (
    | {
        isOpen: boolean;
        onOpenChange: (isOpen: boolean) => void;
      }
    | {
        isOpen?: never;
        onOpenChange?: never;
      }
  );

export const Popconfirm: FC<PopconfirmProps> = (props) => {
  const { t } = useTranslation();
  const {
    id,
    icon = <DefaultIcon color={colorError} size={18} />,
    title,
    description,
    cancelText = t('button.cancel'),
    okText = t('button.ok'),
    onConfirm,
    onCancel,
    children,
    wrapperType,
    style,
    placement,
    stopClickPropagation,
    ...rest
  } = props;
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = typeof rest.isOpen === 'boolean';
  const isOpen = isControlled ? rest.isOpen : internalIsOpen;
  const setIsOpen = isControlled ? rest.onOpenChange : setInternalIsOpen;

  const handleCancel: MouseEventHandler<HTMLElement> = useCallback(
    (event) => {
      if (onCancel?.(event) !== false) {
        setIsOpen(false);
      }
    },
    [onCancel, setIsOpen],
  );

  const handleOk: MouseEventHandler<HTMLElement> = useCallback(
    (event) => {
      if (onConfirm?.(event) !== false) {
        setIsOpen(false);
      }
    },
    [onConfirm, setIsOpen],
  );

  return (
    <Popover
      wrapperType={wrapperType}
      placement={placement}
      style={style}
      stopClickPropagation={stopClickPropagation}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      content={
        <Flex vertical gap="small">
          <Flex gap="small" align="start">
            {icon}
            <div>
              <h5>{title}</h5>
              <div>{description}</div>
            </div>
          </Flex>
          <Flex gap="small" justify="end">
            <Button
              id={`${id}-button-cancel`}
              size="small"
              onClick={handleCancel}
            >
              {cancelText}
            </Button>
            <Button
              id={`${id}-button-ok`}
              size="small"
              onClick={handleOk}
              type="primary"
            >
              {okText}
            </Button>
          </Flex>
        </Flex>
      }
    >
      {children}
    </Popover>
  );
};

const DefaultIcon = styled(Icon.AlertCircle)`
  margin-top: 0.2em;
  flex-shrink: 0;
`;
